import { useState } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getOrgTypeSections,
  getSections,
  linkSectionToOrganizationType,
  unlinkSectionFromOrganizationType,
  updateOrgTypeSection
} from 'wasp/client/operations';
import { Grid3x3, Plus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

interface Props {
  organizationTypeId: string;
  readOnly?: boolean;
}

export default function OrgTypeSectionsTab({ organizationTypeId, readOnly = false }: Props) {
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [enabledByDefault, setEnabledByDefault] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const { data: linkedSections = [], isLoading } = useQuery(getOrgTypeSections, { organizationTypeId });
  const { data: allSections = [] } = useQuery(getSections);

  const linkFn = useAction(linkSectionToOrganizationType);
  const unlinkFn = useAction(unlinkSectionFromOrganizationType);
  const updateFn = useAction(updateOrgTypeSection);

  const linkedSectionIds = new Set((linkedSections as any[]).map((ls: any) => ls.sectionId));

  const availableSections = (allSections as any[]).filter(
    (s: any) => !linkedSectionIds.has(s.id) && !s.archived
  );

  const handleAdd = async () => {
    if (!selectedSectionId) return;
    setIsAdding(true);
    try {
      const result = await linkFn({ organizationTypeId, sectionId: selectedSectionId, enabledByDefault });
      setSelectedSectionId('');
      setEnabledByDefault(false);
      const childCount = (result as any)?.autoAdded?.length ?? 0;
      const description = childCount > 0
        ? `Section added (+ ${childCount} child section${childCount === 1 ? '' : 's'} auto-added).`
        : 'Section added to this organization type.';
      toast({ title: 'Section linked', description });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to link section.', variant: 'destructive' });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (id: string, sectionKey: string) => {
    if (!confirm(`Remove section "${sectionKey}" from this organization type?`)) return;
    try {
      await unlinkFn({ id });
      toast({ title: 'Section removed', description: `"${sectionKey}" unlinked.` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to remove section.', variant: 'destructive' });
    }
  };

  const handleToggleDefault = async (id: string, current: boolean) => {
    try {
      await updateFn({ id, enabledByDefault: !current });
      toast({ title: 'Updated', description: `"Enabled by default" set to ${!current ? 'Yes' : 'No'}.` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update.', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add Section — edit only */}
      {!readOnly && <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Add Section
        </h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
            <select
              value={selectedSectionId}
              onChange={(e) => setSelectedSectionId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Select a section —</option>
              {availableSections.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.section_key}
                  {s.isBase ? ' (base)' : s.isTypeSpecific ? ' (type-specific)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="enabledByDefault"
              checked={enabledByDefault}
              onChange={(e) => setEnabledByDefault(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="enabledByDefault" className="text-sm text-gray-700">Enabled by default</label>
          </div>
          <button
            onClick={handleAdd}
            disabled={!selectedSectionId || isAdding}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
        {availableSections.length === 0 && (
          <p className="mt-3 text-sm text-gray-500">All available sections are already linked to this organization type.</p>
        )}
      </div>}

      {/* Linked Sections Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Grid3x3 className="w-5 h-5" />
          Linked Sections
          <span className="ml-2 text-sm font-normal text-gray-500">({(linkedSections as any[]).length})</span>
        </h3>

        {(linkedSections as any[]).length === 0 ? (
          <div className="text-center py-8">
            <Grid3x3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No sections linked yet.</p>
            {!readOnly && <p className="text-gray-400 text-xs mt-1">Use the form above to add sections to this organization type.</p>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Section Key</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Type</th>
                  <th className="text-center py-2 pr-4 font-medium text-gray-600">Enabled by Default</th>
                  {!readOnly && <th className="text-right py-2 font-medium text-gray-600">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {(linkedSections as any[]).map((ls: any) => (
                  <tr key={ls.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="py-3 pr-4">
                      <span className="font-mono text-gray-900">{ls.section.section_key}</span>
                    </td>
                    <td className="py-3 pr-4">
                      {ls.section.isBase ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Base</span>
                      ) : ls.section.isTypeSpecific ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Type-Specific</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Standard</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-center">
                      {readOnly ? (
                        <span className="inline-flex items-center gap-1 text-sm">
                          {ls.enabledByDefault ? (
                            <>
                              <ToggleRight className="w-5 h-5 text-green-600" />
                              <span className="text-green-700 font-medium">Yes</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-5 h-5 text-gray-400" />
                              <span className="text-gray-500">No</span>
                            </>
                          )}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleToggleDefault(ls.id, ls.enabledByDefault)}
                          className="inline-flex items-center gap-1 text-sm transition-colors"
                          title={`Click to set to ${ls.enabledByDefault ? 'No' : 'Yes'}`}
                        >
                          {ls.enabledByDefault ? (
                            <>
                              <ToggleRight className="w-5 h-5 text-green-600" />
                              <span className="text-green-700 font-medium">Yes</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-5 h-5 text-gray-400" />
                              <span className="text-gray-500">No</span>
                            </>
                          )}
                        </button>
                      )}
                    </td>
                    {!readOnly && (
                      <td className="py-3 text-right">
                        <button
                          onClick={() => handleRemove(ls.id, ls.section.section_key)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



